import { type FC } from 'react';
import { useIntl } from 'react-intl';

// MUI
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { Users } from 'lucide';

// screen
import Icon from 'common/components/Icon';
import { formatDuration } from 'modules/dashboard/helpers/accessStats';
import type { AccessStatisticsResponse } from 'modules/dashboard/types';

interface Props {
  data?: AccessStatisticsResponse | null;
}

const cardSx = {
  px: 2,
  py: 1.5,
  borderRadius: 2.5,
  border: '1px solid',
  borderColor: 'divider',
  boxShadow: '0 8px 24px rgba(22, 28, 82, 0.05)',
} as const;

const Card: FC<{ label: string; value: string; highlight: string }> = ({ label, value, highlight }) => (
  <Paper elevation={0} sx={cardSx}>
    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
      {label}
    </Typography>
    <Typography sx={{ color: highlight, fontWeight: 800, fontSize: 28, lineHeight: 1.2 }}>
      {value}
    </Typography>
  </Paper>
);

/**
 * Tầng 1 của khối "Số lượt truy cập hệ thống": thời gian truy cập trung bình + số người đang
 * online. Khách vãng lai cũng thấy khối này (tài liệu: mọi người xem đầy đủ).
 */
const AccessSummaryCards: FC<Props> = ({ data }) => {
  const { formatMessage } = useIntl();
  const highlight = useTheme().palette.primary.main;

  return (
    <Box>
      <Typography
        variant="subtitle1"
        sx={{ fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}
      >
        <Icon icon={Users} size={16} />
        {formatMessage({ id: 'dashboard.access.sectionTitle' })}
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Card
          label={formatMessage({ id: 'dashboard.access.avgDuration' })}
          value={formatDuration(data?.avg_visit_seconds)}
          highlight={highlight}
        />
        <Card
          label={formatMessage({ id: 'dashboard.access.onlineNow' })}
          value={data ? data.online_now.toLocaleString('vi-VN') : '—'}
          highlight={highlight}
        />
      </Box>
    </Box>
  );
};

export default AccessSummaryCards;
